import { useState } from 'react';
import { IoHome, IoCreate, IoCheckmarkCircle, IoPeople, IoLogoGoogle } from 'react-icons/io5';
import { signInWithGooglePopup } from '../../../utils/firebase.utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notifyBackendOfFirebaseAuth } from '../../../services/authService';
import { showAuthError } from '../../../utils/toast';

const HomeIcon = IoHome as React.ComponentType<{ className?: string }>;
const CreateIcon = IoCreate as React.ComponentType<{ className?: string }>;
const CheckmarkCircleIcon = IoCheckmarkCircle as React.ComponentType<{ className?: string }>;
const PeopleIcon = IoPeople as React.ComponentType<{ className?: string }>;
const LogoGoogleIcon = IoLogoGoogle as React.ComponentType<{ className?: string }>;

const features = [
  {
    icon: <HomeIcon className="text-orange-500" />, bg: 'bg-orange-100', label: 'Build Your College List',
  },
  {
    icon: <CreateIcon className="text-blue-500" />, bg: 'bg-blue-100', label: 'Instant Essay Feedback',
  },
  {
    icon: <CheckmarkCircleIcon className="text-green-500" />, bg: 'bg-green-100', label: 'SMS Application Updates',
  },
  {
    icon: <PeopleIcon className="text-purple-500" />, bg: 'bg-purple-100', label: 'Access Our Live Counselors',
  },
];

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const res = await signInWithGooglePopup();
      
      if (res.user) {
        const idToken = await res.user.getIdToken();
        const data = await notifyBackendOfFirebaseAuth(
          idToken,
          res.user.displayName || '',
          res.user.email || '',
          res.user.photoURL || ''
        );
        
        const userWithBackendData = {
          uid: res.user.uid,
          email: res.user.email || '',
          name: res.user.displayName || '',
          photoURL: res.user.photoURL || '',
          is_new_user: data.is_new_user,
          ...data.user
        };
        
        updateUser(userWithBackendData);
      }
      
      navigate('/');
    } catch (error) {
      showAuthError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="rounded-3xl shadow-2xl p-8" style={{ background: 'var(--surface-primary)' }}>
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center mx-auto mb-6">
            <span className="text-white text-3xl">
              <i className="fas fa-graduation-cap" />
            </span>
          </div>
          
          <h1 className="text-2xl font-bold text-center mb-2 font-inter">
            Thinking with you, not for you
          </h1>
          
          <p className="text-gray-600 text-center mb-6">
            Sign in to unlock powerful tools and features
          </p>
          
          <div className="space-y-4 mb-8">
            {features.map((f, i) => (
              <div key={i} className="flex items-center">
                <span className={`w-7 h-7 rounded-full ${f.bg} flex items-center justify-center mr-3`}>
                  {f.icon}
                </span>
                <span className="text-gray-700 text-base">{f.label}</span>
              </div>
            ))}
          </div>
          
          <button
            onClick={handleLogin}
            disabled={isLoading}
            className={`w-full ${isLoading 
              ? 'bg-blue-500 cursor-not-allowed' 
              : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'
            } text-white font-bold text-base rounded-lg h-12 flex items-center justify-center space-x-2 transition-all`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <LogoGoogleIcon className="text-xl" />
                <span>Login with Google</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 